import { Tooltip } from '@systemeio/ui'
import React, { type JSX } from 'react';
import { CopyToClipboardButton } from 'shared/components/copy-to-clipboard-button'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { TLocoType } from 'shared/hooks/use-loco-translation'
import { UserInterface } from 'shared/types/user-interface'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import ContactInfoTooltip from '../components/contact-info-tooltip'
import ContactStatus from '../components/contact-status'
import ContactTagBubbles from '../components/contact-tag-bubbles'
import { ContactApiInterface, ContactInterface } from '../types/contact-interface'

export const getContactColumns = ({
  renderSelectButton,
  renderSelectAllButton,
  contacts,
  user,
  t,
  width,
}: {
  renderSelectButton: (contact: ContactInterface) => JSX.Element
  renderSelectAllButton: () => JSX.Element
  contacts: ContactApiInterface | undefined
  user?: UserInterface
  t: TLocoType
  width: number
}): ColumnDefinitionInterface<ContactInterface>[] => {
  const columns: ColumnDefinitionInterface<ContactInterface>[] = [
    {
      key: 'registeredAt',
      header: (
        <div className={`flex ${contacts && 'gap-3'}`}>
          <div className="hidden items-center lg:flex">
            {contacts && contacts.items.length !== 0 && renderSelectAllButton()}
          </div>
          <Tooltip
            mode={'overflow'}
            label={t('dashboard.contacts.date_registered')}
            wrapperClassName={'w-full'}
          >
            <span className="truncate">{t('dashboard.contacts.date_registered')}</span>
          </Tooltip>
        </div>
      ),
      onRender: contact => (
        <div className="flex gap-3">
          <div className="flex items-center">{renderSelectButton(contact)}</div>
          <span>
            {dateTimeFormat(contact.registeredAt, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </div>
      ),
      widthPercent: 20,
    },
    {
      key: 'email',
      header: t('global.email_address'),
      widthPercent: 50,
      onRender: contact => (
        <div className={`group flex w-full items-center gap-2 overflow-hidden`}>
          <div className={'h-[48px] w-[48px] min-w-[48px] rounded-full'}>
            <img className={'rounded-full'} src={contact.avatar} alt={'avatar'} />
          </div>
          <div className="flex w-fit flex-col justify-center overflow-hidden">
            <ContactInfoTooltip
              fields={contact.fieldValues}
              referredByEmail={contact.referredBy?.email}
              contactId={contact.id}
            >
              {contact.email}
            </ContactInfoTooltip>
          </div>
          <ContactStatus contact={contact} />
          <CopyToClipboardButton
            className={` hidden group-focus:block sm:group-hover:block`}
            text={contact.email}
          />
        </div>
      ),
    },
    {
      key: 'tags',
      header: t('dashboard.contacts.tags'),
      widthPercent: 30,
      onRender: contact => <ContactTagBubbles tags={contact.tags} />,
    },
  ]

  const columnsMobile: ColumnDefinitionInterface<ContactInterface>[] = [
    {
      key: 'email',
      widthPercent: 50,
      onRender: contact => (
        <div className="w-full">
          <div className={`group flex w-full items-center gap-2 overflow-hidden`}>
            <div>{renderSelectButton(contact)}</div>
            <div className={'h-[48px] w-[48px] min-w-[48px] rounded-full'}>
              <img className={'rounded-full'} src={contact.avatar} alt={'avatar'} />
            </div>
            <div className="flex w-fit flex-col justify-center overflow-hidden">
              <ContactInfoTooltip
                fields={contact.fieldValues}
                referredByEmail={contact.referredBy?.email}
                contactId={contact.id}
              >
                {contact.email}
              </ContactInfoTooltip>
            </div>
            <ContactStatus contact={contact} />
            <CopyToClipboardButton
              className={` hidden group-focus:block sm:group-hover:block`}
              text={contact.email}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'registeredAt',
      onRender: contact => (
        <div className="flex w-full  gap-3">
          <div>
            <Tooltip
              mode={'overflow'}
              label={t('dashboard.contacts.date_registered')}
              wrapperClassName={'w-full'}
            >
              <span className="text-text-primary whitespace-nowrap font-bold">
                {t('dashboard.contacts.date_registered')}
              </span>
            </Tooltip>
          </div>
          <span>
            {dateTimeFormat(contact.registeredAt, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </div>
      ),
      widthPercent: 20,
    },
    {
      key: 'tags',
      header: t('dashboard.contacts.tags'),
      widthPercent: 30,
      onRender: contact => <ContactTagBubbles tags={contact.tags} />,
    },
  ]

  return width > 1024 ? columns : columnsMobile
}
